import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import MriHero from "./MriHero";

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.18em] -mb-[0.18em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 20, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const HIRING_TEXT =
  "Intern - Multimodal Data and Signal Processing · Intern - ML Engineering and Prototype Integration · ";

const HiringNote = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: 0.35, duration: 0.9 }}
    className="mb-9"
    data-testid="hiring-note"
  >
    <Link
      to="/careers"
      className="group pointer-events-auto inline-flex items-center gap-2.5 font-mono-qelp text-[10px] md:text-[11px] tracking-[0.18em] uppercase text-slate-500 hover:text-slate-300 transition-colors duration-300 max-w-full"
      data-testid="hiring-note-link"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#10B8B1] animate-qelp-pulse shrink-0" />
      <span className="text-[#10B8B1] shrink-0">Hiring</span>
      <span
        className="inline-block overflow-hidden max-w-[62vw] md:max-w-[520px]"
        style={{
          maskImage: "linear-gradient(90deg, transparent, black 10%, black 90%, transparent)",
          WebkitMaskImage: "linear-gradient(90deg, transparent, black 10%, black 90%, transparent)",
        }}
      >
        <span className="flex w-max animate-crawl">
          <span className="whitespace-nowrap pr-2" data-testid="hiring-note-role">{HIRING_TEXT}</span>
          <span className="whitespace-nowrap pr-2" aria-hidden="true">{HIRING_TEXT}</span>
        </span>
      </span>
      <span className="text-[#10B8B1] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 shrink-0">→</span>
    </Link>
  </motion.div>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const sceneY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-[#080631] via-[#050422] to-[#080631]"
      data-testid="hero-section"
    >
      <motion.div style={{ y: sceneY }} className="absolute inset-0 opacity-90">
        <MriHero />
      </motion.div>

      <div className="absolute inset-0 bg-[linear-gradient(90deg,#050422_8%,rgba(5,4,34,0.6)_42%,transparent_70%)] pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#050422] to-transparent pointer-events-none" />

      <motion.div style={{ y: contentY, opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 md:px-16 lg:px-24 pt-32 pb-24 w-full pointer-events-none">
        <HiringNote />

        <MaskedLine delay={0.5}>
          <span className="inline-flex items-center gap-3 font-mono-qelp text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#10B8B1]" data-testid="hero-overline">
            <span className="w-8 h-px bg-[#10B8B1]/60" />
            Neurotechnology · Artificial Intelligence · Cognitive Neuroscience
          </span>
        </MaskedLine>

        <h1 className="font-heading font-medium tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl mt-7 max-w-3xl text-slate-50" data-testid="hero-headline">
          <MaskedLine delay={0.65}>The infrastructure for</MaskedLine>
          <MaskedLine delay={0.8}>
            <span className="text-[#10B8B1] italic">cognitive intelligence.</span>
          </MaskedLine>
        </h1>

        <MaskedLine delay={1.0} className="mt-7 max-w-lg text-sm sm:text-base text-slate-400 leading-relaxed">
          <span data-testid="hero-subheadline">
            Human performance depends on cognitive state as much as physical state, yet cognitive state remains the least understood part of performance today. Qelp AI is building toward a way to change that.
          </span>
        </MaskedLine>

        <MaskedLine delay={1.15} className="mt-10 flex flex-wrap items-center gap-4">
          <span className="flex flex-wrap items-center gap-4 pointer-events-auto">
            <button
              onClick={() => go("#manifesto")}
              className="qelp-btn-primary rounded-full px-7 py-3 font-heading font-semibold text-sm tracking-wide"
              data-testid="hero-cta-explore"
            >
              Read the Manifesto
            </button>
            <button
              onClick={() => go("#contact")}
              className="qelp-btn-ghost rounded-full px-7 py-3 font-heading font-semibold text-sm tracking-wide"
              data-testid="hero-cta-contact"
            >
              Get in Touch
            </button>
          </span>
        </MaskedLine>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="mt-16 max-w-xl grid grid-cols-3 gap-6 border-t border-white/10 pt-5"
          data-testid="hero-masthead"
        >
          {[
            ["Field", "Neurotechnology"],
            ["Method", "Evidence-Based"],
            ["Status", "Research Active"],
          ].map(([k, v]) => (
            <span key={k} className="block">
              <span className="block font-mono-qelp text-[9px] tracking-[0.25em] uppercase text-slate-600">{k}</span>
              <span className="block mt-1.5 font-mono-qelp text-[10px] tracking-[0.15em] uppercase text-slate-300">{v}</span>
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        data-testid="hero-scroll-indicator"
      >
        <span className="font-mono-qelp text-[9px] tracking-[0.3em] uppercase text-slate-600">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#10B8B1]/70 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default Hero;
